import { useEffect, useState } from 'react';
import { VictoryPie } from 'victory';
import { IconCoins } from '@tabler/icons-react';
import useRequestState from '../../hooks/useRequestState';
import GetDemographicBreakdownOptions from '../../services/adapters/demographicBreakdownOptions';
import PostDemographicBreakdown from '../../services/adapters/demographicBreakdown';
import { DemographicBreakdownOption, DemographicBreakdownSegment } from '../../types/debt.type';
import { segmentColours, pieChartLabels } from '../../utils/graphColours';
import SimpleDropdown from '../form-inputs/simpleDropdown';
import SegmentButton from './segmentButton';

interface VictorySliceCallbackArgs {
  index?: string | number;
  datum?: { x: string; y: number };
}

interface DemographicBreakdownProps {
  readonly snapshot: string;
  readonly localAuthority: string | number;
  readonly wardWithOther: string;
  readonly debtType: string;
}

export default function DemographicBreakdown({
  snapshot,
  localAuthority,
  wardWithOther,
  debtType,
}: DemographicBreakdownProps) {
  const [selectedFilter, setSelectedFilter] = useState<string>('');
  const [selectedSegment, setSelectedSegment] = useState<string>('');

  const {
    data: options,
    handleRequest: fetchOptions,
    FallbackWrapper: OptionsFallback,
  } = useRequestState<DemographicBreakdownOption[]>();

  const {
    data: segments,
    handleRequest: fetchSegments,
    FallbackWrapper: SegmentsFallback,
  } = useRequestState<DemographicBreakdownSegment[]>();

  useEffect(() => {
    const adapter = new GetDemographicBreakdownOptions();
    fetchOptions(adapter.adaptResponse()).then((pieOptions) => {
      if (pieOptions && pieOptions.length > 0) {
        setSelectedFilter(pieOptions[0].filterName);
      }
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const fetchData = async () => {
      if (!selectedFilter || !snapshot) return;
      const adapter = new PostDemographicBreakdown();
      const fetchedSegments = await fetchSegments(adapter.adaptResponse({
        snapshot,
        localAuthority,
        wardWithOther,
        debtCharacteristicFilterName: selectedFilter,
        debtType,
      }));
      if (fetchedSegments && fetchedSegments.length > 0) {
        setSelectedSegment(fetchedSegments[0].displayName);
      }
    };
    void fetchData();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedFilter, snapshot, localAuthority, wardWithOther, debtType]);

  const filterOptions = (options ?? []).map((option) => ({ value: option.filterName, label: option.displayName }));

  const pieData = (segments ?? []).map((datum) => ({ x: datum.displayName, y: datum.count, label: datum.percentage }));

  return (
    <div className="bg-white border border-pipgrey-border rounded-xl p-5 w-full">
      <div className="flex flex-col md:flex-row items-center justify-between mb-9">
        <div className="flex items-center gap-3">
          <div className="bg-gray-100 p-2 rounded-full">
            <IconCoins className="w-6 h-6 text-primary" />
          </div>
          <h3 className="text-base font-bold text-secondary">Demographic breakdown</h3>
        </div>

        <OptionsFallback loadingMessage="Loading filters...">
          <SimpleDropdown
            id="demographic-filter"
            options={filterOptions}
            selected={selectedFilter}
            onChange={setSelectedFilter}
            className="w-52"
          />
        </OptionsFallback>
      </div>

      <SegmentsFallback loadingMessage="Loading breakdown...">
        <div className="flex flex-col xl:flex-row gap-6 lg:gap-12 items-center">
          <div id="demographic-pie-chart-container" className="flex-1 flex items-center justify-center">
            <div className="w-48 h-48 lg:w-64 lg:h-64">
              <VictoryPie
                data={pieData}
                innerRadius={0}
                labelPlacement="parallel"
                padding={65}
                labels={pieData.map((d) => d.label)}
                style={{
                  data: {
                    fill: (args: VictorySliceCallbackArgs) => segmentColours[Number(args.index ?? 0) % segmentColours.length],
                    stroke: (args: VictorySliceCallbackArgs) => (args.datum?.x === selectedSegment ? '#10B981' : 'none'),
                    strokeWidth: 3,
                    cursor: 'pointer',
                  },
                  labels: pieChartLabels,
                }}
                events={[
                  {
                    target: 'data',
                    eventHandlers: {
                      onClick: (event: unknown, propsArg: VictorySliceCallbackArgs) => {
                        setSelectedSegment(propsArg.datum?.x ?? '');
                        return [];
                      },
                    },
                  },
                ]}
              />
            </div>
          </div>

          <div className="flex-1 flex flex-col gap-3 w-full">
            <p className="text-xs font-semibold text-secondary text-right">Average debt (£)</p>
            <div className="flex flex-col gap-3">
              {(segments ?? []).map((segment, i) => {
                const isSelected = segment.displayName === selectedSegment;
                const colour = segmentColours[i % segmentColours.length];
                return (
                  <SegmentButton
                    key={segment.displayName}
                    segment={segment}
                    isSelected={isSelected}
                    colour={colour}
                    onClick={() => setSelectedSegment(segment.displayName)}
                  />
                );
              })}
            </div>
          </div>
        </div>
      </SegmentsFallback>
    </div>
  );
}
